import React, { useState } from 'react';
import { Card, CardBody, Button, Input, Textarea, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Chip } from '@nextui-org/react';
import HeaderComponent from '../components/General/HeaderComponent';
import { FiMail, FiSend, FiTrash2, FiCornerUpLeft, FiPlus, FiSearch, FiInbox, FiClock, FiStar } from 'react-icons/fi';
import { Notify } from 'notiflix';

export default function MailManagement() {
  const [selectedMail, setSelectedMail] = useState(null);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('inbox'); // inbox, sent, drafts
  const [replyContent, setReplyContent] = useState('');

  // Dummy mail verileri
  const [mails, setMails] = useState([
    {
      id: 1,
      from: 'ahmet.yilmaz@example.com',
      fromName: 'Ahmet Yılmaz',
      subject: 'Proje Güncellemesi Hakkında',
      content: 'Merhaba,\n\nProje ilerlemelerimiz hakkında konuşmak istiyorum. Yarın saat 14:00\'te bir toplantı planlayabilir miyiz?\n\nSaygılarımla,\nAhmet',
      date: '2024-01-15 10:30',
      read: false,
      starred: false,
      folder: 'inbox'
    },
    {
      id: 2,
      from: 'mehmet.demir@example.com',
      fromName: 'Mehmet Demir',
      subject: 'Sunum Materyalleri',
      content: 'Merhaba,\n\nİstediğiniz sunum materyalleri ektedir. Sorularınız olursa çekinmeden sorun.\n\nİyi çalışmalar,\nMehmet',
      date: '2024-01-15 09:15',
      read: true,
      starred: true,
      folder: 'inbox'
    },
    {
      id: 3,
      from: 'ayse.kaya@example.com',
      fromName: 'Ayşe Kaya',
      subject: 'Toplantı Notları',
      content: 'Bugünkü toplantıda konuşulan maddeler:\n\n1. Yeni özellik geliştirmeleri\n2. Test süreçleri\n3. Yayınlama takvimi\n\nDetaylar için ekteki dosyaya bakabilirsiniz.',
      date: '2024-01-14 16:45',
      read: false,
      starred: false,
      folder: 'inbox'
    },
    {
      id: 4,
      from: 'can.ozturk@example.com',
      fromName: 'Can Öztürk',
      subject: 'Bütçe Onayı',
      content: 'Sayın Yetkili,\n\nQ1 bütçesi için onayınızı bekliyoruz. Lütfen onay için geri dönüş yapabilir misiniz?\n\nTeşekkürler,\nCan',
      date: '2024-01-14 14:20',
      read: true,
      starred: false,
      folder: 'inbox'
    },
    {
      id: 5,
      from: 'fatma.arslan@example.com',
      fromName: 'Fatma Arslan',
      subject: 'Müşteri Geri Bildirimi',
      content: 'Müşterilerimizden gelen geri bildirimler oldukça olumlu. Detaylı raporu ekte bulabilirsiniz.',
      date: '2024-01-14 11:30',
      read: true,
      starred: false,
      folder: 'inbox'
    }
  ]);

  const [composeForm, setComposeForm] = useState({
    to: '',
    subject: '',
    content: ''
  });

  // Filtrelenmiş mailler
  const filteredMails = mails.filter(mail => {
    const matchesFolder = mail.folder === activeTab;
    const matchesSearch = searchTerm === '' || 
      mail.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mail.fromName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mail.content.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFolder && matchesSearch;
  });

  const handleCompose = () => {
    setComposeForm({ to: '', subject: '', content: '' });
    setIsComposeOpen(true);
  };

  const handleSendMail = () => {
    if (!composeForm.to || !composeForm.subject || !composeForm.content) {
      Notify.warning('Lütfen tüm alanları doldurun');
      return;
    }

    // Yeni mail gönderildi olarak ekle
    const newMail = {
      id: mails.length + 1,
      from: 'ben@example.com',
      fromName: 'Ben',
      to: composeForm.to,
      subject: composeForm.subject,
      content: composeForm.content,
      date: new Date().toLocaleString('tr-TR'),
      read: true,
      starred: false,
      folder: 'sent'
    };

    setMails([newMail, ...mails]);
    setIsComposeOpen(false);
    setComposeForm({ to: '', subject: '', content: '' });
    Notify.success('Mail başarıyla gönderildi!');
  };

  const handleReply = () => {
    if (!replyContent.trim()) {
      Notify.warning('Lütfen yanıt içeriği girin');
      return;
    }

    const replyMail = {
      id: mails.length + 1,
      from: 'ben@example.com',
      fromName: 'Ben',
      to: selectedMail.from,
      subject: `Re: ${selectedMail.subject}`,
      content: replyContent,
      date: new Date().toLocaleString('tr-TR'),
      read: true,
      starred: false,
      folder: 'sent'
    };

    setMails([replyMail, ...mails]);
    setIsReplyOpen(false);
    setReplyContent('');
    setSelectedMail({ ...selectedMail, read: true });
    Notify.success('Yanıt başarıyla gönderildi!');
  };

  const handleDeleteMail = (mailId) => {
    setMails(mails.filter(mail => mail.id !== mailId));
    if (selectedMail?.id === mailId) {
      setSelectedMail(null);
    }
    Notify.success('Mail silindi');
  };

  const handleToggleStar = (mailId) => {
    setMails(mails.map(mail => 
      mail.id === mailId ? { ...mail, starred: !mail.starred } : mail
    ));
    if (selectedMail?.id === mailId) {
      setSelectedMail({ ...selectedMail, starred: !selectedMail.starred });
    }
  };

  const handleSelectMail = (mail) => {
    setSelectedMail(mail);
    // Mail okundu olarak işaretle
    setMails(mails.map(m => m.id === mail.id ? { ...m, read: true } : m));
  };

  const unreadCount = mails.filter(m => m.folder === 'inbox' && !m.read).length;

  return (
    <div className="w-full dark:bg-dark-100 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 py-6">
        <HeaderComponent title="Mail Yönetimi" />
        
        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          {/* Sol Kolon - Mail Listesi */}
          <div className="lg:col-span-1 space-y-4">
            {/* Yeni Mail Butonu */}
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              startContent={<FiPlus className="w-4 h-4" />}
              onPress={handleCompose}
            >
              Yeni Mail
            </Button>

            {/* Arama */}
            <Input
              placeholder="Mail ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              startContent={<FiSearch className="text-gray-400" />}
              classNames={{
                input: "border border-gray-200 dark:border-dark-400",
                inputWrapper: "border border-gray-200 dark:border-dark-400"
              }}
            />

            {/* Tab'ler */}
            <div className="flex gap-2">
              <Button
                variant={activeTab === 'inbox' ? 'solid' : 'bordered'}
                className={`flex-1 ${activeTab === 'inbox' ? 'bg-blue-600 text-white' : ''}`}
                onPress={() => setActiveTab('inbox')}
                startContent={<FiInbox />}
              >
                Gelen {unreadCount > 0 && `(${unreadCount})`}
              </Button>
              <Button
                variant={activeTab === 'sent' ? 'solid' : 'bordered'}
                className={`flex-1 ${activeTab === 'sent' ? 'bg-blue-600 text-white' : ''}`}
                onPress={() => setActiveTab('sent')}
                startContent={<FiSend />}
              >
                Gönderilen
              </Button>
              <Button
                variant={activeTab === 'drafts' ? 'solid' : 'bordered'}
                className={`flex-1 ${activeTab === 'drafts' ? 'bg-blue-600 text-white' : ''}`}
                onPress={() => setActiveTab('drafts')}
                startContent={<FiClock />}
              >
                Taslaklar
              </Button>
            </div>

            {/* Mail Listesi */}
            <Card className="border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 shadow-none">
              <CardBody className="p-0">
                <div className="divide-y divide-gray-200 dark:divide-dark-400">
                  {filteredMails.length === 0 ? (
                    <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                      <FiMail className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>Mail bulunamadı</p>
                    </div>
                  ) : (
                    filteredMails.map((mail) => (
                      <div
                        key={mail.id}
                        onClick={() => handleSelectMail(mail)}
                        className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-400 transition-colors ${
                          selectedMail?.id === mail.id ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-600' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-sm font-semibold text-[#283252] dark:text-darkWhite-300 ${!mail.read ? 'font-bold' : ''}`}>
                                {mail.fromName || mail.from}
                              </span>
                              {!mail.read && (
                                <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                              )}
                            </div>
                            <p className={`text-sm text-gray-600 dark:text-gray-400 truncate ${!mail.read ? 'font-semibold' : ''}`}>
                              {mail.subject}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                              {mail.date}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              isIconOnly
                              variant="light"
                              size="sm"
                              onPress={(e) => {
                                e.stopPropagation();
                                handleToggleStar(mail.id);
                              }}
                              className={mail.starred ? 'text-yellow-500' : 'text-gray-400'}
                            >
                              <FiStar className={`w-4 h-4 ${mail.starred ? 'fill-current' : ''}`} />
                            </Button>
                            <Button
                              isIconOnly
                              variant="light"
                              size="sm"
                              onPress={(e) => {
                                e.stopPropagation();
                                handleDeleteMail(mail.id);
                              }}
                              className="text-red-400 hover:text-red-600"
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardBody>
            </Card>
          </div>

          {/* Sağ Kolon - Mail Detay */}
          <div className="lg:col-span-2">
            {selectedMail ? (
              <Card className="border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 shadow-none">
                <CardBody className="p-6">
                  {/* Mail Header */}
                  <div className="border-b border-gray-200 dark:border-dark-400 pb-4 mb-4">
                    <div className="flex items-start justify-between mb-2">
                      <h2 className="text-xl font-semibold text-[#283252] dark:text-darkWhite-300">
                        {selectedMail.subject}
                      </h2>
                      <div className="flex items-center gap-2">
                        <Button
                          isIconOnly
                          variant="light"
                          onPress={() => handleToggleStar(selectedMail.id)}
                          className={selectedMail.starred ? 'text-yellow-500' : 'text-gray-400'}
                        >
                          <FiStar className={`w-5 h-5 ${selectedMail.starred ? 'fill-current' : ''}`} />
                        </Button>
                        <Button
                          isIconOnly
                          variant="light"
                          onPress={() => handleDeleteMail(selectedMail.id)}
                          className="text-red-400 hover:text-red-600"
                        >
                          <FiTrash2 className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium text-gray-600 dark:text-gray-400">Gönderen: </span>
                        <span className="text-[#283252] dark:text-darkWhite-300">{selectedMail.fromName || selectedMail.from}</span>
                      </div>
                      {selectedMail.to && (
                        <div>
                          <span className="font-medium text-gray-600 dark:text-gray-400">Alıcı: </span>
                          <span className="text-[#283252] dark:text-darkWhite-300">{selectedMail.to}</span>
                        </div>
                      )}
                      <div>
                        <span className="font-medium text-gray-600 dark:text-gray-400">Tarih: </span>
                        <span className="text-[#283252] dark:text-darkWhite-300">{selectedMail.date}</span>
                      </div>
                    </div>
                  </div>

                  {/* Mail İçeriği */}
                  <div className="mb-6">
                    <div className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                      {selectedMail.content}
                    </div>
                  </div>

                  {/* Yanıtla Butonu */}
                  {activeTab === 'inbox' && (
                    <div className="border-t border-gray-200 dark:border-dark-400 pt-4">
                      <Button
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        startContent={<FiCornerUpLeft className="w-4 h-4" />}
                        onPress={() => setIsReplyOpen(true)}
                      >
                        Yanıtla
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>
            ) : (
              <Card className="border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 shadow-none">
                <CardBody className="p-12">
                  <div className="text-center">
                    <FiMail className="w-24 h-24 mx-auto mb-4 text-gray-400 dark:text-gray-500 opacity-30" />
                    <p className="text-gray-500 dark:text-gray-400 text-lg">
                      Bir mail seçin veya yeni mail oluşturun
                    </p>
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
        </div>

        {/* Yeni Mail Modal */}
        <Modal isOpen={isComposeOpen} onClose={() => setIsComposeOpen(false)} size="2xl" scrollBehavior="inside">
          <ModalContent>
            <ModalHeader>Yeni Mail</ModalHeader>
            <ModalBody>
              <div className="space-y-4">
                <Input
                  label="Alıcı"
                  placeholder="E-posta adresini girin"
                  value={composeForm.to}
                  onChange={(e) => setComposeForm({ ...composeForm, to: e.target.value })}
                  classNames={{
                    input: "border border-gray-200 dark:border-dark-400",
                    inputWrapper: "border border-gray-200 dark:border-dark-400"
                  }}
                />
                <Input
                  label="Konu"
                  placeholder="Mail konusunu girin"
                  value={composeForm.subject}
                  onChange={(e) => setComposeForm({ ...composeForm, subject: e.target.value })}
                  classNames={{
                    input: "border border-gray-200 dark:border-dark-400",
                    inputWrapper: "border border-gray-200 dark:border-dark-400"
                  }}
                />
                <Textarea
                  label="İçerik"
                  placeholder="Mail içeriğinizi yazın"
                  value={composeForm.content}
                  onChange={(e) => setComposeForm({ ...composeForm, content: e.target.value })}
                  minRows={8}
                  classNames={{
                    input: "border border-gray-200 dark:border-dark-400",
                    inputWrapper: "border border-gray-200 dark:border-dark-400"
                  }}
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={() => setIsComposeOpen(false)}>
                İptal
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onPress={handleSendMail}>
                Gönder
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Yanıtla Modal */}
        <Modal isOpen={isReplyOpen} onClose={() => setIsReplyOpen(false)} size="2xl" scrollBehavior="inside">
          <ModalContent>
            <ModalHeader>Yanıtla: {selectedMail?.subject}</ModalHeader>
            <ModalBody>
              <div className="space-y-4">
                <Input
                  label="Alıcı"
                  value={selectedMail?.from || ''}
                  isReadOnly
                  classNames={{
                    input: "border border-gray-200 dark:border-dark-400",
                    inputWrapper: "border border-gray-200 dark:border-dark-400"
                  }}
                />
                <Input
                  label="Konu"
                  value={`Re: ${selectedMail?.subject || ''}`}
                  isReadOnly
                  classNames={{
                    input: "border border-gray-200 dark:border-dark-400",
                    inputWrapper: "border border-gray-200 dark:border-dark-400"
                  }}
                />
                <Textarea
                  label="Yanıt"
                  placeholder="Yanıtınızı yazın"
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  minRows={8}
                  classNames={{
                    input: "border border-gray-200 dark:border-dark-400",
                    inputWrapper: "border border-gray-200 dark:border-dark-400"
                  }}
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={() => setIsReplyOpen(false)}>
                İptal
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onPress={handleReply}>
                Gönder
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </div>
    </div>
  );
}

